import api from './api';
import type { Feature, MenuStructure } from '../types/features';

class FeatureService {
  private features: Feature[] = [];
  private menuStructure: MenuStructure = {};
  private lastFetch: number = 0;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  /**
   * Get user's available features (simplified - all features available for now)
   */
  async getUserFeatures(): Promise<Feature[]> {
    // For now, return empty array as features are handled through menu structure
    // In the future, this can be enhanced to check plan-specific restrictions
    return [];
  }

  /**
   * Get user's menu structure based on their features
   */
  async getUserMenu(): Promise<MenuStructure> {
    const now = Date.now();
    
    // Return cached menu if still valid
    if (Object.keys(this.menuStructure).length > 0 && (now - this.lastFetch) < this.CACHE_DURATION) {
      return this.menuStructure;
    }

    try {
      const response = await api.get('/menu');
      this.menuStructure = response.data;
      this.lastFetch = now;
      return this.menuStructure;
    } catch (error) {
      console.error('Failed to fetch user menu:', error);
      return {};
    }
  }

  /**
   * Check if user has access to a specific feature
   * For now, all features are available (can be enhanced with plan-specific logic)
   */
  async hasFeature(featureName: string): Promise<boolean> {
    // For now, all features are available unless explicitly restricted
    // In the future, this can check plan-specific restrictions
    return true;
  }

  /**
   * Check if user has access to a specific menu item
   */
  async hasMenuAccess(menuPath: string): Promise<boolean> {
    const menu = await this.getUserMenu();
    
    // Check root level menu items
    if (menu.root) {
      const hasRootAccess = menu.root.some(item => item.path === menuPath);
      if (hasRootAccess) return true;
    }

    // Check submenu items
    for (const [parent, items] of Object.entries(menu)) {
      if (parent !== 'root' && Array.isArray(items)) {
        const hasSubmenuAccess = items.some(item => item.path === menuPath);
        if (hasSubmenuAccess) return true;
      }
    }

    return false;
  }

  /**
   * Get features by category (simplified - returns empty for now)
   */
  async getFeaturesByCategory(category: Feature['category']): Promise<Feature[]> {
    // For now, return empty array as features are handled through menu structure
    return [];
  }

  /**
   * Get menu features only
   */
  async getMenuFeatures(): Promise<Feature[]> {
    return this.getFeaturesByCategory('menu');
  }

  /**
   * Get functionality features only
   */
  async getFunctionalityFeatures(): Promise<Feature[]> {
    return this.getFeaturesByCategory('functionality');
  }

  /**
   * Get storage features only
   */
  async getStorageFeatures(): Promise<Feature[]> {
    return this.getFeaturesByCategory('storage');
  }

  /**
   * Get integration features only
   */
  async getIntegrationFeatures(): Promise<Feature[]> {
    return this.getFeaturesByCategory('integration');
  }

  /**
   * Get support features only
   */
  async getSupportFeatures(): Promise<Feature[]> {
    return this.getFeaturesByCategory('support');
  }

  /**
   * Clear cache (useful when user's plan changes)
   */
  clearCache(): void {
    this.features = [];
    this.menuStructure = {};
    this.lastFetch = 0;
  }

  /**
   * Get feature limits for a specific feature (simplified - returns null for now)
   */
  async getFeatureLimits(featureName: string): Promise<Record<string, any> | null> {
    // For now, return null as limits are not implemented
    return null;
  }

  /**
   * Check if a feature has specific limit (simplified - returns false for now)
   */
  async hasFeatureLimit(featureName: string, limitKey: string): Promise<boolean> {
    // For now, return false as limits are not implemented
    return false;
  }

  /**
   * Get feature limit value (simplified - returns null for now)
   */
  async getFeatureLimitValue(featureName: string, limitKey: string): Promise<any> {
    // For now, return null as limits are not implemented
    return null;
  }
}

// Create and export a singleton instance
const featureService = new FeatureService();
export default featureService;
